import { NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { LoginComponent } from "./pages/auth/login/login.component";
import { HeaderComponent } from "./components/header/header.component";
import { LogoComponent } from "./pages/auth/components/logo/logo.component";
import { CreateAccountComponent } from "./pages/auth/components/create-account/create-account.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SmsSendComponent } from "./pages/auth/sms-send/sms-send.component";
import { IConfig, NgxMaskDirective, provideEnvironmentNgxMask } from "ngx-mask";
import { ToastrModule } from "ngx-toastr";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { MainPageComponent } from "./pages/main-page/main-page.component";
import { RecoveryPasswordComponent } from "./pages/auth/recovery-password/recovery-password.component";
import { RequestComponent } from "./pages/auth/recovery-password/request/request.component";
import { RequestSendComponent } from "./pages/auth/recovery-password/request-send/request-send.component";
import { ResetPasswordComponent } from "./pages/auth/reset-password/reset-password.component";
import { UpdatePasswordComponent } from "./pages/auth/reset-password/update-password/update-password.component";
import { ResetSuccessComponent } from "./pages/auth/reset-password/reset-success/reset-success.component";
import { LangSelectComponent } from "./pages/auth/components/lang-select/lang-select.component";
import { RegisterComponent } from "./pages/auth/register/register.component";
import { PersonalDataComponent } from "./pages/auth/register/personal-data/personal-data.component";
import { ProductsComponent } from "./pages/auth/register/products/products.component";
import { AgreementComponent } from "./pages/auth/register/agreement/agreement.component";
import { ProgressComponent } from "./pages/auth/components/progress/progress.component";
import { BackButtonComponent } from "./pages/auth/components/back-button/back-button.component";
import { AgreementPageComponent } from "./pages/agreement/agreement-page.component";
import { RegisterSuccessComponent } from "./pages/auth/register/register-success/register-success.component";
import { NgxFileDropModule } from "ngx-file-drop";
import { CompanyDataComponent } from "./pages/auth/register/company-data/company-data.component";
import { AddressCompanyComponent } from "./pages/auth/register/address-company/address-company.component";
import { environment } from "../environments/environment";
import { LocaleInterceptor } from "./interceptors/locale.interceptor";
import { MenuComponent } from "./components/menu/menu.component";
import { NavigationComponent } from "./components/navigation/navigation.component";
import { MobileMenuComponent } from "./components/mobile-menu/mobile-menu.component";
import { AnalyticsComponent } from "./pages/analytics/analytics.component";
import { UserListComponent } from "./pages/user-list/user-list.component";
import { NotificationsComponent } from "./pages/notifications/notifications.component";
import { RoleListComponent } from "./pages/role-list/role-list.component";
import { PaymentListComponent } from "./pages/payment-list/payment-list.component";
import { RecurringPaymentListComponent } from "./pages/recurring-payment-list/recurring-payment-list.component";
import { ReturnListComponent } from "./pages/return-list/return-list.component";
import { InformationComponent } from "./pages/information/information.component";
import { SavedCardListComponent } from "./pages/saved-card-list/saved-card-list.component";
import { TerminalListComponent } from "./pages/terminal-list/terminal-list.component";
import { VirtualCashListComponent } from "./pages/virtual-cash-list/virtual-cash-list.component";
import { PaymentLinkListComponent } from "./pages/payment-link-list/payment-link-list.component";
import { PaymentToCardComponent } from "./pages/payment-to-card/payment-to-card.component";
import { RoleItemComponent } from "./pages/role-list/components/role-item/role-item.component";
import { RoleContextMenuComponent } from "./pages/role-list/components/role-context-menu/role-context-menu.component";
import { ModalComponent } from "./components/modal/modal.component";
import { DeleteRoleDialogComponent } from "./pages/role-list/components/delete-role-dialog/delete-role-dialog.component";
import { AddRoleDialogComponent } from "./pages/role-list/components/add-role-dialog/add-role-dialog.component";
import { TokenInterceptor } from "./interceptors/token.interceptor";
import { CheckboxBlockComponent } from "./components/checkbox-block/checkbox-block.component";
import { CheckboxItemComponent } from "./components/checkbox-item/checkbox-item.component";
import { EditRoleDialogComponent } from "./pages/role-list/components/edit-role-dialog/edit-role-dialog.component";
import { NotificationComponent } from "./components/notification/notification.component";
import { HttpErrorInterceptor } from "./interceptors/http-error.interceptor";
import { PaymentPageFilterComponent } from "./pages/payment-list/components/payment-page-filter/payment-page-filter.component";
import { PaymentPageTableComponent } from "./pages/payment-list/components/payment-page-table/payment-page-table.component";
import { FilterButtonComponent } from "./components/filter-button/filter-button.component";
import { FilterBlockComponent } from "./components/filter-block/filter-block.component";
import { UserPageFilterComponent } from "./pages/user-list/components/user-page-filter/user-page-filter.component";
import { FilterSelectComponent } from "./components/filter-select/filter-select.component";
import { CheckboxTableComponent } from "./components/checkbox-table/checkbox-table.component";
import { ContextMenuComponent } from "./components/context-menu/context-menu.component";
import { UserContextMenuComponent } from "./pages/user-list/components/user-context-menu/user-context-menu.component";
import { DatepickerComponent } from "./components/datepicker/datepicker.component";
import { PeriodComponent } from "./components/period/period.component";
import { AddUserModalComponent } from "./pages/user-list/components/add-user-modal/add-user-modal.component";
import { EditUserModalComponent } from "./pages/user-list/components/edit-user-modal/edit-user-modal.component";
import { FullNamePipe } from "./pipes/full-name.pipe";
import { CheckboxAllTableComponent } from "./components/checkbox-all-table/checkbox-all-table.component";
import { DeleteUsersDialogComponent } from "./pages/user-list/components/delete-users-dialog/delete-users-dialog.component";
import { ResetSessionDialogComponent } from "./pages/user-list/components/reset-session-dialog/reset-session-dialog.component";
import { ChangeStatusDialogComponent } from "./pages/user-list/components/change-status-dialog/change-status-dialog.component";
import { ResetPasswordDialogComponent } from "./pages/user-list/components/reset-password-dialog/reset-password-dialog.component";
import { PaymentLinksListFilterComponent } from "./pages/payment-link-list/components/payment-links-list-filter/payment-links-list-filter.component";
import { AmountRangeComponent } from "./components/amount-range/amount-range.component";
import { FilterInputComponent } from "./components/filter-input/filter-input.component";
import { SwitchTableComponent } from "./components/switch-table/switch-table.component";
import { PaylinkContextMenuComponent } from "./pages/payment-link-list/components/paylink-context-menu/paylink-context-menu.component";
import { AddPaymentLinkDialogComponent } from "./pages/payment-link-list/components/add-payment-link-dialog/add-payment-link-dialog.component";
import { FormModalSelectComponent } from "./components/form-modal-select/form-modal-select.component";
import { PaymentDetailDialogComponent } from "./pages/payment-list/components/payment-detail-dialog/payment-detail-dialog.component";
import { ReturnPaymentDialogComponent } from "./pages/payment-list/components/return-payment-dialog/return-payment-dialog.component";
import { DeletePaymentSuccessDialogComponent } from "./pages/payment-list/components/delete-payment-success-diaolog/delete-payment-success-dialog.component";
import { ReturnDetailComponentComponent } from "./pages/return-list/components/return-detail-component/return-detail-component.component";
import { CopyReturnDetailSuccessComponent } from "./pages/return-list/components/copy-return-detail-success/copy-return-detail-success.component";
import { ReturnPageFilterComponent } from "./pages/return-list/components/return-page-filter/return-page-filter.component";
import { RecurringPageFilterComponent } from "./pages/recurring-payment-list/components/recurring-page-filter/recurring-page-filter.component";
import { CardItemComponent } from "./components/card-item/card-item.component";
import { CustomSelectComponent } from "./components/custom-select/custom-select.component";
import { DeletePaylinkSuccessDialogComponent } from "./pages/payment-link-list/components/delete-paylink-success-dialog/delete-paylink-success-dialog.component";
import { DeletePaylinkDialogComponent } from "./pages/payment-link-list/components/delete-paylink-dialog/delete-paylink-dialog.component";
import { EditPaylinkDialogComponent } from "./pages/payment-link-list/components/edit-paylink-dialog/edit-paylink-dialog.component";
import { DatePipe } from "@angular/common";
import { CustomCurrencyPipe } from "./pipes/custom-currency.pipe";
import { SendMailPaylinkDialogComponent } from "./pages/payment-link-list/components/send-mail-paylink-dialog/send-mail-paylink-dialog.component";
import { AddNewCardDialogComponent } from "./pages/saved-card-list/components/add-new-card-dialog/add-new-card-dialog.component";
import { DeleteCardDialogComponent } from "./pages/saved-card-list/components/delete-card-dialog/delete-card-dialog.component";
import { DeleteCardSuccessDialogComponent } from "./pages/saved-card-list/components/delete-card-success-dialog/delete-card-success-dialog.component";
import { AddCardSuccessDialogComponent } from "./pages/saved-card-list/components/add-card-success-dialog/add-card-success-dialog.component";
import { SavedCardContextMenuComponent } from "./pages/saved-card-list/components/saved-card-context-menu/saved-card-context-menu.component";
import { PaymentViewListComponent } from "./pages/payment-view-list/payment-view-list.component";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { VirtualPosContextMenuComponent } from "./pages/virtual-cash-list/components/virtual-pos-context-menu/virtual-pos-context-menu.component";
import { NotificationItemComponent } from "./pages/notifications/components/notification-item/notification-item.component";
import { TerminalPageFilterComponent } from "./pages/terminal-list/components/terminal-page-filter/terminal-page-filter.component";
import { VirtualCashPageFilterComponent } from "./pages/virtual-cash-list/components/virtual-cash-page-filter/virtual-cash-page-filter.component";
import { EditVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/edit-virtual-cash-dialog/edit-virtual-cash-dialog.component";
import { CommissionVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/commission-virtual-cash-dialog/commission-virtual-cash-dialog.component";
import { FileNameInterceptor } from "./interceptors/file-name.interceptor";
import { DeleteVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/delete-virtual-cash-dialog/delete-virtual-cash-dialog.component";
import { DeleteGroupVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/delete-group-virtual-cash-dialog/delete-group-virtual-cash-dialog.component";
import { ProfileComponent } from "./pages/profile/profile.component";
import { SupportComponent } from "./pages/support/support.component";
import { ChangePasswordDialogComponent } from "./pages/profile/components/change-password-dialog/change-password-dialog.component";
import { AddPaymentViewDialogComponent } from "./pages/payment-view-list/components/add-payment-view-dialog/add-payment-view-dialog.component";
import { EditPaymentViewDialogComponent } from "./pages/payment-view-list/components/edit-payment-view-dialog/edit-payment-view-dialog.component";
import { PaymentViewPageFilterComponent } from "./pages/payment-view-list/components/payment-view-page-filter/payment-view-page-filter.component";
import { NotificationPageFilterComponent } from "./pages/notifications/components/notification-page-filter/notification-page-filter.component";
import { DeleteItemNotificationDialogComponent } from "./pages/notifications/components/delete-item-notification-dialog/delete-item-notification-dialog.component";
import { DeleteAllNotificationDialogComponent } from "./pages/notifications/components/delete-all-notification-dialog/delete-all-notification-dialog.component";
import { ContactingSupportItemComponent } from "./pages/support/components/contacting-support-item/contacting-support-item.component";
import { ContactingSupportDetailComponent } from "./pages/support/components/contacting-support-detail/contacting-support-detail.component";
import { SupportPageFilterComponent } from "./pages/support/components/support-page-filter/support-page-filter.component";
import { AddContactingDialogComponent } from "./pages/support/components/add-contacting-dialog/add-contacting-dialog.component";
import { HasAccessDirective } from "./directives/has-access.directive";
import { PaymentsAnalyticsComponent } from "./pages/analytics/components/payments-analytics/payments-analytics.component";
import { ReturnsAnalyticsComponent } from "./pages/analytics/components/returns-analytics/returns-analytics.component";
import { DeletePaymentDialogComponent } from "./pages/payment-view-list/components/delete-payment-dialog/delete-payment-dialog.component";
import { ToggleBlockVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/toggle-block-virtrual-cash-dialog/toggle-block-virtrual-cash-dialog.component";
import { ToggleGroupBlockVirtualCashDialogComponent } from "./pages/virtual-cash-list/components/toggle-group-block-virtrual-cash-dialog/toggle-group-block-virtrual-cash-dialog.component";
import { RequestsAnalyticsComponent } from "./pages/analytics/components/requests-analytics/requests-analytics.component";
import { SupportAnalyticsComponent } from "./pages/analytics/components/support-analytics/support-analytics.component";
import { PaymentPageAnalyticsComponent } from "./pages/analytics/components/payment-page-analytics/payment-page-analytics.component";
import { FormCustomSelectComponent } from "./components/form-custom-select/form-custom-select.component";
import { FormCustomDatepickerComponent } from "./components/form-custom-datepicker/form-custom-datepicker.component";
import { FormCustomSearchComponent } from "./components/form-custom-search/form-custom-search.component";
import { SwitchTableWithDialogComponent } from "./components/switch-table-with-dialog/switch-table-with-dialog.component";
import { SwitchConfirmDialogComponent } from "./components/switch-table-with-dialog/components/switch-confirm-dialog/switch-confirm-dialog.component";
import { TimerPipe } from "./pipes/timer.pipe";
import { GlobalLoaderComponent } from "./components/global-loader/global-loader.component";
import { GlobalLoaderInterceptor } from "./interceptors/global-loader.interceptor";
import { LoadingDotsDirective } from "./directives/load-dots.directive";

export function HttpLoaderFactory(http: HttpClient): TranslateLoader {
	return new TranslateHttpLoader(http, "./assets/locale/", ".json");
}

const maskConfig: Partial<IConfig> = {
	validation: false,
};

@NgModule({
	declarations: [
		AppComponent,
		LoginComponent,
		HeaderComponent,
		LogoComponent,
		CreateAccountComponent,
		SmsSendComponent,
		MainPageComponent,
		RecoveryPasswordComponent,
		RequestComponent,
		RequestSendComponent,
		ResetPasswordComponent,
		UpdatePasswordComponent,
		ResetSuccessComponent,
		LangSelectComponent,
		RegisterComponent,
		PersonalDataComponent,
		ProductsComponent,
		AgreementComponent,
		ProgressComponent,
		BackButtonComponent,
		AgreementPageComponent,
		RegisterSuccessComponent,
		CompanyDataComponent,
		AddressCompanyComponent,
		MenuComponent,
		NavigationComponent,
		MobileMenuComponent,
		AnalyticsComponent,
		UserListComponent,
		NotificationsComponent,
		RoleListComponent,
		PaymentListComponent,
		RecurringPaymentListComponent,
		ReturnListComponent,
		InformationComponent,
		SavedCardListComponent,
		TerminalListComponent,
		VirtualCashListComponent,
		PaymentLinkListComponent,
		PaymentToCardComponent,
		RoleItemComponent,
		RoleContextMenuComponent,
		ModalComponent,
		DeleteRoleDialogComponent,
		AddRoleDialogComponent,
		CheckboxBlockComponent,
		CheckboxItemComponent,
		EditRoleDialogComponent,
		NotificationComponent,
		PaymentPageFilterComponent,
		PaymentPageTableComponent,
		FilterButtonComponent,
		FilterBlockComponent,
		UserPageFilterComponent,
		FilterSelectComponent,
		CheckboxTableComponent,
		ContextMenuComponent,
		UserContextMenuComponent,
		DatepickerComponent,
		PeriodComponent,
		AddUserModalComponent,
		EditUserModalComponent,
		FullNamePipe,
		CheckboxAllTableComponent,
		DeleteUsersDialogComponent,
		ResetSessionDialogComponent,
		ChangeStatusDialogComponent,
		ResetPasswordDialogComponent,
		PaymentLinksListFilterComponent,
		AmountRangeComponent,
		FilterInputComponent,
		SwitchTableComponent,
		PaylinkContextMenuComponent,
		AddPaymentLinkDialogComponent,
		FormModalSelectComponent,
		PaymentDetailDialogComponent,
		ReturnPaymentDialogComponent,
		DeletePaymentSuccessDialogComponent,
		ReturnDetailComponentComponent,
		CopyReturnDetailSuccessComponent,
		ReturnPageFilterComponent,
		RecurringPageFilterComponent,
		CardItemComponent,
		CustomSelectComponent,
		DeletePaylinkSuccessDialogComponent,
		DeletePaylinkDialogComponent,
		EditPaylinkDialogComponent,
		CustomCurrencyPipe,
		SendMailPaylinkDialogComponent,
		AddNewCardDialogComponent,
		DeleteCardDialogComponent,
		DeleteCardSuccessDialogComponent,
		AddCardSuccessDialogComponent,
		SavedCardContextMenuComponent,
		PaymentViewListComponent,
		VirtualPosContextMenuComponent,
		NotificationItemComponent,
		TerminalPageFilterComponent,
		VirtualCashPageFilterComponent,
		EditVirtualCashDialogComponent,
		CommissionVirtualCashDialogComponent,
		DeleteVirtualCashDialogComponent,
		DeleteGroupVirtualCashDialogComponent,
		ProfileComponent,
		SupportComponent,
		ChangePasswordDialogComponent,
		AddPaymentViewDialogComponent,
		EditPaymentViewDialogComponent,
		PaymentViewPageFilterComponent,
		NotificationPageFilterComponent,
		DeleteItemNotificationDialogComponent,
		DeleteAllNotificationDialogComponent,
		ContactingSupportItemComponent,
		ContactingSupportDetailComponent,
		SupportPageFilterComponent,
		AddContactingDialogComponent,
		HasAccessDirective,
		PaymentsAnalyticsComponent,
		ReturnsAnalyticsComponent,
		DeletePaymentDialogComponent,
		ToggleBlockVirtualCashDialogComponent,
		ToggleGroupBlockVirtualCashDialogComponent,
		RequestsAnalyticsComponent,
		SupportAnalyticsComponent,
		PaymentPageAnalyticsComponent,
		FormCustomSelectComponent,
		FormCustomDatepickerComponent,
		FormCustomSearchComponent,
		SwitchTableWithDialogComponent,
		SwitchConfirmDialogComponent,
		TimerPipe,
		GlobalLoaderComponent,
		LoadingDotsDirective,
	],
	imports: [
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		FormsModule,
		ReactiveFormsModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient],
			},
			useDefaultLang: false,
		}),
		NgxMaskDirective,
		ToastrModule.forRoot(),
		BrowserAnimationsModule,
		NgxFileDropModule,
		ClipboardModule,
	],
	providers: [
		provideEnvironmentNgxMask(maskConfig),
		{ provide: "BACKEND_API_URL", useValue: environment.backendApiUrl },
		{ provide: HTTP_INTERCEPTORS, multi: true, useClass: LocaleInterceptor },
		{ provide: HTTP_INTERCEPTORS, multi: true, useClass: TokenInterceptor },
		{ provide: HTTP_INTERCEPTORS, multi: true, useClass: HttpErrorInterceptor },
		{ provide: HTTP_INTERCEPTORS, multi: true, useClass: FileNameInterceptor },
		{ provide: HTTP_INTERCEPTORS, useClass: GlobalLoaderInterceptor, multi: true },
		DatePipe,
	],
	bootstrap: [AppComponent],
})
export class AppModule {}
